import { useEffect, useMemo, useState } from 'react';
import CompanyPeriodSelector from '../../../shared/components/CompanyPeriodSelector';
import PageHeader from '../../../shared/components/PageHeader';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import {
  fetchMacroInstrumentSeries,
  fetchMacroModules,
  refreshMacroData
} from '../../../services/macroData';
import '../macroDataShared.css';

const FX_COLORS = ['#77a7ff', '#ffbf69', '#4de3c2', '#ff7aa2'];

const UI_TEXT = {
  'tr-TR': {
    title: 'Altin',
    description: 'Altin Istatistikleri izleme modulu.',
    refreshing: 'Yenileniyor...',
    refreshEvds: 'EVDS Verilerini Yenile',
    goldSeriesLoadError: 'Altin serisi yuklenemedi.',
    macroLoadError: 'Makro veri yuklenemedi.',
    macroRefreshError: 'Makro veri yenilenemedi.',
    loading: 'Yukleniyor...',
    moduleNotFound: 'Makro modul bulunamadi.',
    noSeriesInModule: 'Bu modulde gosterilebilir seri bulunmuyor.',
    na: 'N/A',
    format: 'Bicim',
    report: 'Rapor',
    level: 'Duzey',
    yoyPct: 'Yillik Yuzde Degisim',
    diff: 'Fark',
    frequency: 'Frekans',
    formula: 'Formul',
    transformation: 'Donusum',
    start: 'Baslangic',
    end: 'Bitis',
    series: 'Seriler',
    selectedSeries: 'seri secili',
    generating: 'Olusturuluyor...',
    buildChart: 'Grafigi Olustur',
    chartDataMissing: 'Grafik verisi bulunmuyor.',
    chartAria: 'Altin zaman serisi',
    period: 'Donem',
    date: 'Tarih',
    goldPricesTitle: 'Altin Fiyatlari (Ankara Kuyumcular ve Saatciler Odasi)',
    goldSeriesTitle: 'Altin Fiyatlari Serisi',
    freqOriginal: 'Orijinal',
    freqDaily: 'Gunluk',
    freqBusinessDay: 'Isgunu',
    freqWeekly: 'Haftalik',
    freqBiMonthly: 'Ayda 2 Kez',
    freqMonthly: 'Aylik',
    freqQuarterly: '3 Aylik',
    freqSemiAnnual: '6 Aylik',
    freqYearly: 'Yillik',
    aggDefault: 'Varsayilan',
    aggAverage: 'Ortalama',
    aggMin: 'En Dusuk',
    aggMax: 'En Yuksek',
    aggFirst: 'Ilk',
    aggLast: 'Son',
    aggSum: 'Toplam'
  },
  'en-US': {
    title: 'Gold',
    description: 'Gold Statistics monitoring module.',
    refreshing: 'Refreshing...',
    refreshEvds: 'Refresh EVDS Data',
    goldSeriesLoadError: 'Gold series could not be loaded.',
    macroLoadError: 'Macro data could not be loaded.',
    macroRefreshError: 'Macro data could not be refreshed.',
    loading: 'Loading...',
    moduleNotFound: 'Macro module not found.',
    noSeriesInModule: 'No displayable series found in this module.',
    na: 'N/A',
    format: 'Format',
    report: 'Report',
    level: 'Level',
    yoyPct: 'Year-over-Year % Change',
    diff: 'Difference',
    frequency: 'Frequency',
    formula: 'Formula',
    transformation: 'Transformation',
    start: 'Start',
    end: 'End',
    series: 'Series',
    selectedSeries: 'series selected',
    generating: 'Generating...',
    buildChart: 'Build Chart',
    chartDataMissing: 'No chart data found.',
    chartAria: 'Gold time series',
    period: 'Period',
    date: 'Date',
    goldPricesTitle: 'Gold Prices (Ankara Jewelers and Watchmakers Chamber)',
    goldSeriesTitle: 'Gold Price Series',
    freqOriginal: 'Original',
    freqDaily: 'Daily',
    freqBusinessDay: 'Business Day',
    freqWeekly: 'Weekly',
    freqBiMonthly: 'Twice Monthly',
    freqMonthly: 'Monthly',
    freqQuarterly: 'Quarterly',
    freqSemiAnnual: 'Semi-Annual',
    freqYearly: 'Yearly',
    aggDefault: 'Default',
    aggAverage: 'Average',
    aggMin: 'Minimum',
    aggMax: 'Maximum',
    aggFirst: 'First',
    aggLast: 'Last',
    aggSum: 'Sum'
  }
};

const FREQUENCY_OPTIONS = [
  { value: '', key: 'freqOriginal' },
  { value: '1', key: 'freqDaily' },
  { value: '2', key: 'freqBusinessDay' },
  { value: '3', key: 'freqWeekly' },
  { value: '4', key: 'freqBiMonthly' },
  { value: '5', key: 'freqMonthly' },
  { value: '6', key: 'freqQuarterly' },
  { value: '7', key: 'freqSemiAnnual' },
  { value: '8', key: 'freqYearly' }
];

const FORMULA_OPTIONS = [
  { value: '0', key: 'level' },
  { value: '1', key: 'yoyPct' },
  { value: '2', key: 'diff' },
  { value: '3', key: 'yoyPct' },
  { value: '4', key: 'diff' }
];

const AGGREGATION_OPTIONS = [
  { value: '', key: 'aggDefault' },
  { value: 'avg', key: 'aggAverage' },
  { value: 'min', key: 'aggMin' },
  { value: 'max', key: 'aggMax' },
  { value: 'first', key: 'aggFirst' },
  { value: 'last', key: 'aggLast' },
  { value: 'sum', key: 'aggSum' }
];

const formatNumber = (value, locale = 'tr-TR', digits = 2, suffix = '') => {
  if (value === null || value === undefined || value === '') return '-';
  const number = Number(value);
  if (Number.isNaN(number)) return '-';
  return `${number.toLocaleString(locale, { maximumFractionDigits: digits, minimumFractionDigits: digits })}${suffix}`;
};

const formatSignedPct = (value, locale = 'tr-TR') => {
  if (value === null || value === undefined || value === '') return '-';
  const number = Number(value);
  if (Number.isNaN(number)) return '-';
  return `${number > 0 ? '+' : ''}${number.toLocaleString(locale, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}%`;
};

const formatDateTime = (value, locale = 'tr-TR') => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString(locale);
};

const inferFrequencyFromPoints = (points) => {
  if (!Array.isArray(points) || points.length < 2) return '';
  const dates = points
    .map((point) => new Date(point.timestamp))
    .filter((date) => !Number.isNaN(date.getTime()))
    .sort((a, b) => a.getTime() - b.getTime());
  if (dates.length < 2) return '';

  const monthDiffs = [];
  const dayDiffs = [];
  let allFirstDay = true;
  for (let index = 1; index < dates.length; index += 1) {
    const previous = dates[index - 1];
    const current = dates[index];
    monthDiffs.push((current.getFullYear() - previous.getFullYear()) * 12 + (current.getMonth() - previous.getMonth()));
    dayDiffs.push(Math.round((current.getTime() - previous.getTime()) / 86400000));
    if (current.getDate() !== 1 || previous.getDate() !== 1) allFirstDay = false;
  }

  const allEqual = (values, expected) => values.every((value) => value === expected);
  if (allFirstDay && allEqual(monthDiffs, 12)) return '8';
  if (allFirstDay && allEqual(monthDiffs, 6)) return '7';
  if (allFirstDay && allEqual(monthDiffs, 3)) return '6';
  if (allFirstDay && allEqual(monthDiffs, 1)) return '5';
  if (dayDiffs.every((diff) => diff >= 6 && diff <= 8)) return '3';
  return '';
};

const formatPeriod = (value, frequency, inferredFrequency, locale = 'tr-TR') => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const paddedMonth = String(month).padStart(2, '0');
  const effectiveFrequency = String(frequency || inferredFrequency || '');

  switch (effectiveFrequency) {
    case '5':
      return `${year}-${paddedMonth}`;
    case '6':
      return `Q${Math.floor((month - 1) / 3) + 1} ${year}`;
    case '7':
      return `${month <= 6 ? 'H1' : 'H2'} ${year}`;
    case '8':
      return `${year}`;
    default:
      return date.toLocaleDateString(locale);
  }
};

const toInputDate = (date) => {
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const MultiTrendChart = ({ seriesList, frequency, language, t }) => {
  const normalized = (seriesList || []).map((item) => ({
    ...item,
    points: (item.points || []).filter((point) => Number.isFinite(Number(point.value)))
  })).filter((item) => item.points.length > 0);

  if (!normalized.length) return <p className="macro-data-empty">{t('chartDataMissing')}</p>;

  const width = 960;
  const height = 300;
  const left = 44;
  const right = 18;
  const top = 16;
  const bottom = 30;
  const w = width - left - right;
  const h = height - top - bottom;

  const allValues = normalized.flatMap((item) => item.points.map((point) => Number(point.value)));
  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  const range = Math.max(max - min, 0.000001);

  const yOf = (value) => top + ((max - Number(value)) / range) * h;
  const gridLines = 5;
  const axisPoints = normalized[0]?.points || [];
  const inferredFrequency = inferFrequencyFromPoints(axisPoints);
  const axisTickCount = Math.min(6, axisPoints.length);
  const axisTickIndexes = axisTickCount <= 1
    ? [0]
    : [...new Set(Array.from({ length: axisTickCount }, (_, i) => Math.round((i * (axisPoints.length - 1)) / (axisTickCount - 1))))];
  const axisXOf = (pointIndex) => left + (axisPoints.length === 1 ? w / 2 : (pointIndex / (axisPoints.length - 1)) * w);

  return (
    <div>
      <div className="macro-fx-legend">
        {normalized.map((item, index) => (
          <span key={item.instrument || item.title}>
            <i style={{ backgroundColor: FX_COLORS[index % FX_COLORS.length] }} />
            {item.title}
          </span>
        ))}
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="macro-data-chart" role="img" aria-label={t('chartAria')}>
        <defs>
          <linearGradient id="macroChartShade" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#9db8ff" stopOpacity="0.08" />
            <stop offset="100%" stopColor="#9db8ff" stopOpacity="0.01" />
          </linearGradient>
        </defs>
        <rect x={left} y={top} width={w} height={h} fill="url(#macroChartShade)" />
        {Array.from({ length: gridLines + 1 }).map((_, index) => {
          const y = top + (h / gridLines) * index;
          return <line key={`grid-${index}`} x1={left} y1={y} x2={left + w} y2={y} className="macro-axis-grid" />;
        })}
        <line x1={left} y1={top} x2={left} y2={top + h} className="macro-axis" />
        <line x1={left} y1={top + h} x2={left + w} y2={top + h} className="macro-axis" />
        {normalized.map((item, index) => {
          const xOf = (pointIndex) => left + (item.points.length === 1 ? w / 2 : (pointIndex / (item.points.length - 1)) * w);
          const line = item.points.map((point, pointIndex) => `${xOf(pointIndex)},${yOf(point.value)}`).join(' ');
          const stroke = FX_COLORS[index % FX_COLORS.length];
          return (
            <g key={`${item.instrument || item.title}-${index}`}>
              <polyline points={line} fill="none" stroke={stroke} strokeWidth="2.9" strokeLinecap="round" strokeLinejoin="round" className="macro-multi-line" />
              {item.points.map((point, pointIndex) => (
                <circle key={`${item.instrument}-${point.timestamp}-${pointIndex}`} cx={xOf(pointIndex)} cy={yOf(point.value)} r="3.2" fill={stroke} className="macro-multi-point">
                  <title>{`${item.title} | ${t('period')}: ${formatPeriod(point.timestamp, frequency, inferredFrequency, language)} | ${t('date')}: ${formatDateTime(point.timestamp, language)} | ${formatNumber(point.value, language, 4)}`}</title>
                </circle>
              ))}
            </g>
          );
        })}
        {axisTickIndexes.map((pointIndex) => {
          const x = axisXOf(pointIndex);
          const point = axisPoints[pointIndex];
          return (
            <g key={`x-tick-${pointIndex}`}>
              <line x1={x} y1={top + h} x2={x} y2={top + h + 4} className="macro-axis" />
              <text x={x} y={height - 6} textAnchor="middle" className="macro-axis-label">
                {formatPeriod(point?.timestamp, frequency, inferredFrequency, language)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

function CfoGoldPage() {
  const { language } = useLanguage();
  const text = UI_TEXT[language] || UI_TEXT['tr-TR'];
  const t = (key) => text[key] || UI_TEXT['tr-TR'][key] || key;

  const [selection, setSelection] = useState({ companyId: '', periodId: '' });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState('');
  const [goldModule, setGoldModule] = useState(null);
  const [activeInstrument, setActiveInstrument] = useState('');
  const [goldLoading, setGoldLoading] = useState(false);
  const [goldSeriesSet, setGoldSeriesSet] = useState(null);
  const [goldFilters, setGoldFilters] = useState(() => {
    const now = new Date();
    const start = new Date();
    start.setFullYear(start.getFullYear() - 3);
    return {
      format: 'json',
      report: 'LEVEL',
      startDate: toInputDate(start),
      endDate: toInputDate(now),
      frequency: '',
      formula: '0',
      aggregationType: '',
      instruments: ['GOLD_REPUBLIC_SELL_TRY', 'GOLD_BULLION_SELL_TRY_GR', 'GOLD_LONDON_OUNCE_SELL_USD', 'GOLD_RESAT_SELL_TRY']
    };
  });

  const hasSelection = Boolean(selection.companyId && selection.periodId);
  const configuredModuleIndicators = useMemo(
    () => (goldModule?.indicators || []).filter((item) => item.snapshot?.source !== 'UNCONFIGURED'),
    [goldModule]
  );
  const goldSeriesSummary = `${goldFilters.instruments.length} ${t('selectedSeries')}`;

  const loadGoldModule = async (companyId, periodId) => {
    const payload = await fetchMacroModules(companyId, periodId);
    const foundGoldModule = (payload || []).find((module) => module.key === 'gold') || null;
    setGoldModule(foundGoldModule);
    const initialInstrument = foundGoldModule?.indicators?.find((item) => item.snapshot?.source !== 'UNCONFIGURED')?.instrument
      || foundGoldModule?.indicators?.[0]?.instrument
      || '';
    setActiveInstrument(initialInstrument);
  };

  const handleGoldApply = async () => {
    if (!hasSelection || !goldFilters.startDate || !goldFilters.endDate) return;
    setGoldLoading(true);
    setMessage('');
    try {
      const formulaMap = { LEVEL: '0', YOY_PCT: '3', DIFF: '2' };
      const payload = await fetchMacroInstrumentSeries(selection.companyId, selection.periodId, {
        instruments: goldFilters.instruments,
        startDate: goldFilters.startDate,
        endDate: goldFilters.endDate,
        frequency: goldFilters.frequency || undefined,
        formula: formulaMap[goldFilters.report] || goldFilters.formula || undefined,
        aggregationType: goldFilters.aggregationType || undefined,
        title: t('goldPricesTitle')
      });
      setGoldSeriesSet(payload);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : text.goldSeriesLoadError);
      setGoldSeriesSet(null);
    } finally {
      setGoldLoading(false);
    }
  };

  useEffect(() => {
    if (!hasSelection) {
      setGoldModule(null);
      setActiveInstrument('');
      setGoldSeriesSet(null);
      return;
    }
    const loadModules = async () => {
      setLoading(true);
      setMessage('');
      try {
        await loadGoldModule(selection.companyId, selection.periodId);
      } catch (error) {
        setMessage(error instanceof Error ? error.message : text.macroLoadError);
        setGoldModule(null);
        setActiveInstrument('');
      } finally {
        setLoading(false);
      }
    };
    loadModules();
  }, [hasSelection, selection.companyId, selection.periodId, text.macroLoadError]);

  useEffect(() => {
    const available = configuredModuleIndicators.map((item) => item.instrument);
    if (!available.length) return;
    setGoldFilters((prev) => {
      const selected = prev.instruments.filter((item) => available.includes(item));
      return { ...prev, instruments: selected.length ? selected : available.slice(0, Math.min(4, available.length)) };
    });
  }, [configuredModuleIndicators]);

  useEffect(() => {
    if (!hasSelection || !goldModule || !goldFilters.instruments.length || goldSeriesSet) return;
    const loadInitialGold = async () => { await handleGoldApply(); };
    loadInitialGold();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSelection, goldModule, goldFilters.instruments.length, goldSeriesSet, selection.companyId, selection.periodId]);

  const handleRefresh = async () => {
    if (!hasSelection) return;
    setRefreshing(true);
    setMessage('');
    try {
      await refreshMacroData(selection.companyId, selection.periodId);
      await loadGoldModule(selection.companyId, selection.periodId);
      await handleGoldApply();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : text.macroRefreshError);
    } finally {
      setRefreshing(false);
    }
  };

  const handleGoldInstrumentToggle = (instrument) => {
    setGoldFilters((prev) => {
      const exists = prev.instruments.includes(instrument);
      if (exists) return { ...prev, instruments: prev.instruments.filter((item) => item !== instrument) };
      return { ...prev, instruments: [...prev.instruments, instrument] };
    });
  };

  return (
    <div className="card macro-data-page">
      <PageHeader
        title={t('title')}
        description={t('description')}
        actions={<button type="button" className="btn-primary" onClick={handleRefresh} disabled={!hasSelection || refreshing || loading}>{refreshing ? t('refreshing') : t('refreshEvds')}</button>}
      />

      <CompanyPeriodSelector onSelectionChange={setSelection} locale={language} />
      {message && <p className="macro-data-message">{message}</p>}

      <section className="macro-indicator-grid">
        {!goldModule ? (
          <p className="macro-data-empty">{loading ? t('loading') : t('moduleNotFound')}</p>
        ) : configuredModuleIndicators.length === 0 ? (
          <p className="macro-data-empty">{t('noSeriesInModule')}</p>
        ) : configuredModuleIndicators.map((item) => (
          <button key={item.instrument} type="button" className={`macro-indicator-card ${activeInstrument === item.instrument ? 'active' : ''}`} onClick={() => setActiveInstrument(item.instrument)}>
            <strong>{item.title}</strong>
            <span>{formatNumber(item.snapshot?.value, language, 4)}</span>
            <small>{formatSignedPct(item.snapshot?.changePct, language)}</small>
            <p>{item.snapshot?.source || t('na')} - {formatDateTime(item.snapshot?.sourceTimestamp, language)}</p>
          </button>
        ))}
      </section>

      <section className="macro-fx-filters">
        <div className="macro-fx-row">
          <label>{t('format')}
            <select value={goldFilters.format} onChange={(event) => setGoldFilters((prev) => ({ ...prev, format: event.target.value }))}>
              <option value="json">json</option>
            </select>
          </label>
          <label>{t('report')}
            <select value={goldFilters.report} onChange={(event) => setGoldFilters((prev) => ({ ...prev, report: event.target.value }))}>
              <option value="LEVEL">{t('level')}</option>
              <option value="YOY_PCT">{t('yoyPct')}</option>
              <option value="DIFF">{t('diff')}</option>
            </select>
          </label>
          <label>{t('frequency')}
            <select value={goldFilters.frequency} onChange={(event) => setGoldFilters((prev) => ({ ...prev, frequency: event.target.value }))}>
              {FREQUENCY_OPTIONS.map((option) => <option key={option.value || 'default'} value={option.value}>{t(option.key)}</option>)}
            </select>
          </label>
          <label>{t('formula')}
            <select value={goldFilters.formula} onChange={(event) => setGoldFilters((prev) => ({ ...prev, formula: event.target.value }))}>
              {FORMULA_OPTIONS.map((option) => <option key={`${option.value}-${option.key}`} value={option.value}>{t(option.key)}</option>)}
            </select>
          </label>
          <label>{t('transformation')}
            <select value={goldFilters.aggregationType} onChange={(event) => setGoldFilters((prev) => ({ ...prev, aggregationType: event.target.value }))}>
              {AGGREGATION_OPTIONS.map((option) => <option key={option.value || 'default'} value={option.value}>{t(option.key)}</option>)}
            </select>
          </label>
        </div>
        <div className="macro-fx-row">
          <label>{t('start')}
            <input type="date" value={goldFilters.startDate} onChange={(event) => setGoldFilters((prev) => ({ ...prev, startDate: event.target.value }))} />
          </label>
          <label>{t('end')}
            <input type="date" value={goldFilters.endDate} onChange={(event) => setGoldFilters((prev) => ({ ...prev, endDate: event.target.value }))} />
          </label>
          <details className="macro-multi-select">
            <summary>{t('series')}<span>{goldSeriesSummary}</span></summary>
            <div className="macro-multi-select-menu">
              {configuredModuleIndicators.map((item) => (
                <label key={item.instrument}>
                  <input type="checkbox" checked={goldFilters.instruments.includes(item.instrument)} onChange={() => handleGoldInstrumentToggle(item.instrument)} />
                  {item.title}
                </label>
              ))}
            </div>
          </details>
          <button type="button" className="btn-primary macro-fx-apply" onClick={handleGoldApply} disabled={goldLoading || !goldFilters.instruments.length}>
            {goldLoading ? t('generating') : t('buildChart')}
          </button>
        </div>
      </section>

      <section className="macro-series-panel">
        <div className="macro-series-head">
          <h3>{`${goldSeriesSet?.title || t('goldSeriesTitle')}`}</h3>
          <span>{`${goldSeriesSet?.startDate || '-'} - ${goldSeriesSet?.endDate || '-'}`}</span>
        </div>
        <MultiTrendChart seriesList={goldSeriesSet?.series || []} frequency={goldFilters.frequency} language={language} t={t} />
      </section>
    </div>
  );
}

export default CfoGoldPage;
